import { useLocation } from '@tanstack/react-router';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import { isEmptyString } from '@/utils/string';
import { LegacyRedirectReporter } from './-legacy-redirects';

/**
 * Redirect-only Journeys URLs (FPS-2346): old addresses kept alive for bookmarks, marketing
 * links, and anything else still pointing at them. Each has no page of its own — it exists
 * only to forward.
 *
 * Three invariants hold across all of them:
 *
 * - **`SafeNavigate`, never plain `navigate()`.** The embedded Journeys app serves every
 *   target (the Outbox tabs too, whenever the outbox rollout flag is off). A plain
 *   `navigate()` swaps the address bar without running the child-app handshake, leaving a
 *   booted embedded instance rendering its previous route under the new URL.
 * - **The history entry is replaced, not pushed**, so Back returns wherever the user came
 *   from rather than bouncing through the stub again.
 * - **The query string and fragment carry across**, matching the other legacy forwards in
 *   `-legacy-redirects.tsx`.
 */

/** Every target below is workspace-scoped. All but Message activity sit under the Journeys bundle prefix. */
const JOURNEYS_ROOT_TEMPLATE = '/workspaces/:workspaceId/journeys';

const DASHBOARD_TEMPLATE = `${JOURNEYS_ROOT_TEMPLATE}/dashboard`;
const AUTOMATIONS_TEMPLATE = `${JOURNEYS_ROOT_TEMPLATE}/automations`;
// Message activity is a Hydra page without the `journeys` segment.
const MESSAGE_ACTIVITY_TEMPLATE = '/workspaces/:workspaceId/message_activity';

export type JourneysRedirectLocation = {
  /** The raw `workspaceId` route segment, `last` sentinel included. */
  workspaceId: string;
  /** The query string with its leading `?`, or an empty string. */
  search: string;
  /** The fragment with its leading `#`, or an empty string. */
  hash: string;
};

/** TanStack's `location.hash` may omit the leading `#`; normalize it for concatenation. */
export function toHashSuffix(hash: string): string {
  if (isEmptyString(hash)) {
    return '';
  }

  return hash.startsWith('#') ? hash : `#${hash}`;
}

/** Fill `:workspaceId` in a route template and append the query string + fragment. */
export function buildJourneysRedirectTarget(
  template: string,
  { workspaceId, search, hash }: JourneysRedirectLocation,
): string {
  return `${template.replace(':workspaceId', workspaceId)}${search}${toHashSuffix(hash)}`;
}

/**
 * `/journeys/v2/<rest>` → `/journeys/<rest>`: drop the `v2` prefix, keep the rest of the path.
 * A bare `/journeys/v2` lands on the Journeys root, which is what dropping the prefix leaves.
 */
export function buildV2RedirectTemplate(splat: string): string {
  const rest = splat.replace(/^\/+/, '').replace(/\/+$/, '');

  return rest.length > 0 ? `${JOURNEYS_ROOT_TEMPLATE}/${rest}` : JOURNEYS_ROOT_TEMPLATE;
}

/**
 * The forward keeps whatever workspace scope the incoming URL named, `last` sentinel included,
 * so the target resolves it exactly as it would have had the user landed there directly.
 */
function useJourneysRedirectLocation(workspaceIdParam: WorkspaceIdRouteParamInput): JourneysRedirectLocation {
  const location = useLocation();

  return {
    workspaceId: normalizeWorkspaceIdRouteParam(workspaceIdParam),
    search: location.searchStr,
    hash: location.hash,
  };
}

type JourneysRedirectProps = {
  /** A route template containing `:workspaceId`. */
  template: string;
  workspaceId: WorkspaceIdRouteParamInput;
};

function JourneysRedirect({ template, workspaceId }: JourneysRedirectProps) {
  const location = useJourneysRedirectLocation(workspaceId);

  return (
    <LegacyRedirectReporter>
      <SafeNavigate
        to={buildJourneysRedirectTarget(template, location)}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    </LegacyRedirectReporter>
  );
}

export type JourneysRedirectRouteProps = {
  workspaceId: WorkspaceIdRouteParamInput;
};

/** `/journeys/drafts` — the top-level Drafts URL from before it moved under the Outbox. */
export function DraftsRedirectRoute({ workspaceId }: JourneysRedirectRouteProps) {
  return <JourneysRedirect template={`${MESSAGE_ACTIVITY_TEMPLATE}/drafts`} workspaceId={workspaceId} />;
}

/** `/journeys/deliveries` — the top-level Deliveries URL from before it moved under the Outbox. */
export function DeliveriesRedirectRoute({ workspaceId }: JourneysRedirectRouteProps) {
  return <JourneysRedirect template={`${MESSAGE_ACTIVITY_TEMPLATE}/sent`} workspaceId={workspaceId} />;
}

/** `/journeys/triggered` — the pre-v2 name for the campaigns list. */
export function TriggeredRedirectRoute({ workspaceId }: JourneysRedirectRouteProps) {
  return <JourneysRedirect template={AUTOMATIONS_TEMPLATE} workspaceId={workspaceId} />;
}

/** `/journeys/triggered/new` — the pre-v2 name for the campaign create page. */
export function TriggeredNewRedirectRoute({ workspaceId }: JourneysRedirectRouteProps) {
  return <JourneysRedirect template={`${AUTOMATIONS_TEMPLATE}/new`} workspaceId={workspaceId} />;
}

export type V2RedirectRouteProps = JourneysRedirectRouteProps & {
  /** The path after `/journeys/v2/`. */
  splat: string;
};

/**
 * `/journeys/v2/*` — URLs from before the v2 app dropped its path prefix. These had been
 * dead-ending: the forward they used to go through targeted a `/env/:environmentId/…` address
 * that the embedded app's router no longer registers under the `/workspaces/:id` URL scheme.
 */
export function V2RedirectRoute({ workspaceId, splat }: V2RedirectRouteProps) {
  return <JourneysRedirect template={buildV2RedirectTemplate(splat)} workspaceId={workspaceId} />;
}

/**
 * `/journeys/setup-guide` and everything under it → the dashboard. This is the
 * bookmark/marketing-link stub only; the real setup guide lives in workspace settings.
 */
export function SetupGuideRedirectRoute({ workspaceId }: JourneysRedirectRouteProps) {
  return <JourneysRedirect template={DASHBOARD_TEMPLATE} workspaceId={workspaceId} />;
}
